import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { VStack, Box, Text, IconButton, useTheme } from '@chakra-ui/react';
import { X } from 'lucide-react';

import type { ChakraTheme } from '@duke-hq/ui/theme/chakraTheme';
import useDashboardContext from '../../../context/useDashboardContext';
import ErrorFallback from '../../ErrorFallback';
import Loader from './Loader';
import { iconSize } from '@duke-hq/ui/constants/scalable';

interface Props {
  title: string;
  children: React.ReactNode;
}

export default function ContentContainer({ title, children }: Props) {
  const theme = useTheme<ChakraTheme>();
  const { closePanel } = useDashboardContext();

  return (
    <VStack
      height={`calc(100vh - ${theme.components.Dashboard.sizes.sidePanelTopOffset})`}
      w="full"
      spacing="2"
    >
      <Box w="full" display="flex" justifyContent="space-between" alignItems="center">
        <Text
          fontSize="lg"
          fontWeight="semibold"
          color="fg.emphasis"
          lineHeight="7"
          letterSpacing="0%"
          verticalAlign="middle"
        >
          {title}
        </Text>
        <IconButton
          aria-label="Close"
          icon={<X size={iconSize.lg} />}
          variant="ghost"
          color="fg.subtle"
          size="sm"
          onClick={closePanel}
        />
      </Box>
      <Box w="full" bg="bg.surface" borderRadius="2xl" p="4" pt={2} overflowY="auto" flex="1">
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Suspense fallback={<Loader />}>{children}</Suspense>
        </ErrorBoundary>
      </Box>
    </VStack>
  );
}
